/**
 * Hiring Service
 * AIDLC Administrative Backend
 *
 * API calls for the Agent New Hire workflow.
 * Maps to /api/hiring endpoints.
 */

import apiClient from './api';

export interface HireProposal {
  id: string;
  agentId: string;
  agentName: string;
  fullName: string;
  role: string;
  expertise: string[];
  catchphrase: string;
  yearsExp: number;
  avatarColor: string;
  instructionContent: string;
  proposedBy: string;
  status: 'draft' | 'hr_review' | 'director_review' | 'ceo_approval' | 'approved' | 'rejected' | 'provisioning' | 'active';
  hrReview: {
    reviewedBy: string | null;
    reviewedAt: string | null;
    scores: {
      personaQuality: number;
      expertiseRelevance: number;
      teamComplementarity: number;
      instructionQuality: number;
      cultureFit: number;
    } | null;
    totalScore: number | null;
    recommendation: string | null;
    notes: string | null;
  };
  directorReview: {
    reviewedBy: string | null;
    reviewedAt: string | null;
    recommendation: string | null;
    notes: string | null;
  };
  ceoDecision: {
    decision: 'approved' | 'rejected' | null;
    decidedAt: string | null;
    notes: string | null;
  };
  createdAt: string;
  updatedAt: string;
}

export interface HiringNeed {
  id: string;
  title: string;
  role: string;
  reason: string;
  requestedBy: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'submitted' | 'reviewing' | 'approved' | 'converted' | 'declined';
  assignedTo: string | null;
  proposalId: string | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

const HIRING_PATH = '/hiring';

export async function getAllProposals(status?: string): Promise<HireProposal[]> {
  const params = status ? { status } : {};
  const response = await apiClient.get<HireProposal[]>(`${HIRING_PATH}/proposals`, { params });
  return response.data;
}

export async function getProposal(id: string): Promise<HireProposal> {
  const response = await apiClient.get<HireProposal>(`${HIRING_PATH}/proposals/${id}`);
  return response.data;
}

export async function createProposal(data: {
  agentId: string;
  agentName: string;
  fullName?: string;
  role: string;
  expertise?: string[];
  catchphrase?: string;
  yearsExp?: number;
  avatarColor?: string;
  instructionContent?: string;
  proposedBy?: string;
}): Promise<HireProposal> {
  const response = await apiClient.post<HireProposal>(`${HIRING_PATH}/proposals`, data);
  return response.data;
}

export async function updateProposalStatus(id: string, status: string): Promise<HireProposal> {
  const response = await apiClient.put<HireProposal>(`${HIRING_PATH}/proposals/${id}/status`, { status });
  return response.data;
}

export async function submitHrReview(id: string, data: {
  scores: { personaQuality: number; expertiseRelevance: number; teamComplementarity: number; instructionQuality: number; cultureFit: number };
  recommendation: string;
  notes?: string;
}): Promise<HireProposal> {
  const response = await apiClient.put<HireProposal>(`${HIRING_PATH}/proposals/${id}/hr-review`, data);
  return response.data;
}

export async function submitDirectorReview(id: string, data: {
  recommendation: string;
  notes?: string;
}): Promise<HireProposal> {
  const response = await apiClient.put<HireProposal>(`${HIRING_PATH}/proposals/${id}/director-review`, data);
  return response.data;
}

export async function submitCeoDecision(id: string, decision: 'approved' | 'rejected', notes?: string): Promise<HireProposal> {
  const response = await apiClient.put<HireProposal>(`${HIRING_PATH}/proposals/${id}/ceo-decision`, { decision, notes });
  return response.data;
}

export async function provisionAgent(id: string): Promise<{ proposal: HireProposal; agent: unknown }> {
  const response = await apiClient.post(`${HIRING_PATH}/proposals/${id}/provision`);
  return response.data;
}

// =============================================
// Hiring Needs API
// =============================================

export async function getAllNeeds(status?: string): Promise<HiringNeed[]> {
  const params = status ? { status } : {};
  const response = await apiClient.get<HiringNeed[]>(`${HIRING_PATH}/needs`, { params });
  return response.data;
}

export async function createNeed(data: {
  title: string;
  role: string;
  reason: string;
  requestedBy?: string;
  priority?: string;
  notes?: string;
}): Promise<HiringNeed> {
  const response = await apiClient.post<HiringNeed>(`${HIRING_PATH}/needs`, data);
  return response.data;
}

export async function updateNeed(id: string, updates: Partial<HiringNeed>): Promise<HiringNeed> {
  const response = await apiClient.put<HiringNeed>(`${HIRING_PATH}/needs/${id}`, updates);
  return response.data;
}

export async function deleteNeed(id: string): Promise<void> {
  await apiClient.delete(`${HIRING_PATH}/needs/${id}`);
}
